import React, { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { logAuthEvent } from '../utils/logging';
import ResetPassword from './ResetPassword';
import './Login.css';



const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState('');
  const [showFailedLoginModal, setShowFailedLoginModal] = useState(false);

  useEffect(() => {
    // Don't redirect if we're coming from signup or if preventAuthRedirect is set
    if (currentUser && !location.state?.preventAuthRedirect && !location.state?.fromSignup) {
      if (!currentUser.emailVerified) {
        navigate('/email-sent', { 
          state: { 
            email: currentUser.email?.toLowerCase(),
            isNewAccount: false,
            fromSignup: false
          },
          replace: true
        });
      } else {
        navigate('/register', { replace: true });
      }
    }
  }, [currentUser, navigate, location.state]);

  useEffect(() => {
    // Check if user is coming from email verification
    const state = location.state as { verificationSuccess?: boolean; email?: string };
    if (state?.verificationSuccess) {
      setSuccess('Email verified successfully! Please log in to continue.');
      setEmail(state.email || '');
      // Clear the state
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();

    try {
      setLoading(true);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Log successful login attempts for security reporting.
      logAuthEvent({
        event: 'login_success',
        email: normalizedEmail,
        userId: userCredential.user.uid,
        details: {
          emailVerified: userCredential.user.emailVerified,
          providerData: userCredential.user.providerData?.map(provider => provider.providerId) || []
        }
      }).catch((logErr) => console.error('Auth log failed:', logErr));
      
      // The user is now signed in, check email verification status
      if (!auth.currentUser?.emailVerified) {
        navigate('/email-sent', { 
          state: { 
            email: normalizedEmail,
            isNewAccount: false,
            fromSignup: false
          }
        });
      } else {
        navigate('/register');
      }
    } catch (err: any) {
      console.error('Login error:', err);

      // Log failed login attempts with Firebase error details.
      logAuthEvent({
        event: 'login_failure',
        email: normalizedEmail,
        error: err?.message || 'Unknown login error',
        details: {
          errorCode: err?.code || 'unknown',
          errorName: err?.name || 'unknown'
        }
      }).catch((logErr) => console.error('Auth log failed:', logErr));
      setShowFailedLoginModal(true);
    } finally {
      setLoading(false);
    }
  };

  if (isResetting) {
    return <ResetPassword onBack={() => setIsResetting(false)} />;
  }

  return (
    <div className="auth-container">
      <h2>Login</h2>
      {success && <div className="success-message">{success}</div>}
      
      <div className="login-info-box">
        <p><strong>First time using this online tool?</strong></p>
        <p>If this is your first time you will need to create an account before logging in.</p>
        <button 
          className="link-button primary-signup"
          onClick={() => navigate('/signup')}
          disabled={loading}
        >
          Create New Account
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div>
          <label>Email:</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            placeholder="Enter your email"
          />
        </div>
        <div className="password-field">
          <label>Password:</label>
          <div className="password-input-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              placeholder="Enter your password"
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              disabled={loading}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </div>
        <button type="submit" disabled={loading}>
          {loading ? 'Logging in...' : 'Login'}
        </button>
      </form>
      <div className="auth-links">
        <button 
          className="link-button"
          onClick={() => setIsResetting(true)}
          disabled={loading}
        >
          Forgot Password?
        </button>
      </div>
      {showFailedLoginModal && (
        <div className="login-modal-backdrop" role="presentation">
          <div className="login-modal" role="dialog" aria-modal="true" aria-labelledby="failed-login-title">
            <h3 id="failed-login-title">Unable to sign in</h3>
            <p>
              The email or password you entered is incorrect. If you forgot your password, use Forgot Password to reset it.
            </p>
            <div className="login-modal-actions">
              <button
                type="button"
                className="login-modal-secondary"
                onClick={() => setShowFailedLoginModal(false)}
              >
                Try Again
              </button>
              <button
                type="button"
                className="login-modal-primary"
                onClick={() => {
                  setShowFailedLoginModal(false);
                  setIsResetting(true);
                }}
              >
                Forgot Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login; 